import { FunctionComponent, useContext, useMemo } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Bath from 'design-system/Iconography/SVG/Bath'
import Bed from 'design-system/Iconography/SVG/Bed'
import Ruler from 'design-system/Iconography/SVG/Ruler'
import Body4 from 'design-system/Typography/Body/Body4'
import Heading4 from 'design-system/Typography/Heading/Heading4'

import Spacer from 'components/Spacer'

import { ImageTypeEnum, getImageUrl } from 'services/images'

import { formatAddress } from 'utils/listingHelpers'
import { getListingCardBarText } from 'utils/statusHelpers'
import { addCommas } from 'utils/stringFormatting'

import * as T from 'types'
import * as E from 'types/enums'

interface IClickProp {
  onClick?: () => void
}
const ViewListingCardContainer = styled.div<IClickProp>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 288px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    height: 334px;
  }
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.radii[24]}px;
  box-shadow: ${props => props.theme.shadows.neutral.level4};

  &:hover {
    box-shadow: ${props => props.theme.shadows.neutral.level5};
    transform: scale(1.005);
  }

  ${props => props.onClick && 'cursor: pointer;'}

  transition: all 0.2s ease-in-out;
`

const PhotoBottomBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  padding: 0 18px;
  width: auto;
  height: 50px;
  background: ${props => props.theme.colors.black.alpha(0.5)};
  mix-blend-mode: normal;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  border-radius: ${props => props.theme.radii[24]}px ${props => props.theme.radii[24]}px 0 0;
`

const Photo = styled.img`
  position: absolute;
  object-fit: cover;
  width: 100%;
  height: 100%;
  ${props => `border-top-left-radius: ${props.theme.radii[24]}px;`}
  ${props => `border-top-right-radius: ${props.theme.radii[24]}px;`}
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${props => props.theme.spacings[8]}px ${props => props.theme.spacings[16]}px
    ${props => props.theme.spacings[16]}px;
`

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
`

const PriceRow = styled(InfoRow)`
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
`

const DatumWithIcon = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;

  p {
    padding-top: 1px; /* font correction */
  }
`

const IconContainer = styled.div`
  height: 24px;
  width: 24px;
  border-radius: 100%;
  background-color: ${props => props.theme.colors.blue50};
  display: flex;
  align-items: center;
  justify-content: center;
`

// as weird as it looks this is actually well supported
// https://css-tricks.com/line-clampin/#aa-the-standardized-way
const AddressRow = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

interface IProps {
  listing: T.IListing
  onClick?: () => void
}

const HomesForSaleListingCard: FunctionComponent<IProps> = ({ listing, onClick }) => {
  const theme = useContext(ThemeContext)

  const formattedPrice = useMemo(
    () => `$${addCommas(String(listing.displayPrice))}`,
    [listing.displayPrice],
  )

  const primaryPhoto = listing.photos?.[0]
  const photoSrc = useMemo(
    () => getImageUrl(listing._id, primaryPhoto, ImageTypeEnum.ListingCard),
    [primaryPhoto?.name, primaryPhoto?.url],
  )

  const baths = useMemo(() => {
    let bathWithHalf = listing.bath ? listing.bath : 0

    if (listing.bathHalf) {
      bathWithHalf += listing.bathHalf ? 0.5 : 0
    }

    return bathWithHalf
  }, [listing.bath, listing.bathHalf])

  const renderOverlay = useMemo(() => {
    const barText = getListingCardBarText(listing)
    return () => (barText ? <PhotoBottomBar>{barText}</PhotoBottomBar> : null)
  }, [listing])

  const alt = useMemo(() => {
    const address = formatAddress(listing)
    return `Exterior of ${address}`
  }, [listing])

  const onClickProps = onClick ? { onClick } : {}

  return (
    <ViewListingCardContainer {...onClickProps}>
      <ImageContainer>
        <Photo
          src={photoSrc}
          alt={alt}
          loading="lazy"
        />
        {renderOverlay()}
      </ImageContainer>
      <InfoContainer>
        <PriceRow>
          <Heading4>{formattedPrice}</Heading4>
        </PriceRow>
        <Spacer size={theme.spacings[4]} />
        <InfoRow>
          {listing.propertyType !== E.HomeTypeEnum.land && (
            <>
              <DatumWithIcon>
                <IconContainer>
                  <Bed
                    fill={theme.colors.blue500}
                    width={15}
                    height={15}
                  />
                </IconContainer>
                <Body4>{`${listing.bed} Bed`}</Body4>
              </DatumWithIcon>
              <Spacer size={12} />
              <DatumWithIcon>
                <IconContainer>
                  <Bath
                    fill={theme.colors.blue500}
                    width={15}
                    height={15}
                  />
                </IconContainer>
                <Body4>{`${baths} Bath`} </Body4>
              </DatumWithIcon>
              <Spacer size={12} />
              <DatumWithIcon>
                <IconContainer>
                  <Ruler
                    fill={theme.colors.blue500}
                    width={15}
                    height={15}
                  />
                </IconContainer>
                {/* Only show the sqft if it's less than 5 chars, otherwise we get line breaks */}
                <Body4>{`${listing.sqft} ${Number(listing.sqft) > 9999 ? '' : 'Sqft'}`}</Body4>
              </DatumWithIcon>
            </>
          )}
          {listing.propertyType === E.HomeTypeEnum.land && (
            <Body4 tag="span">{`${listing.lotSize || 0} ${listing.lotSizeType || 'Acres'}`}</Body4>
          )}
        </InfoRow>
        <Spacer size={theme.spacings[8]} />
        <AddressRow>
          <Body4>{formatAddress(listing, true)}</Body4>
        </AddressRow>
      </InfoContainer>
    </ViewListingCardContainer>
  )
}

export default HomesForSaleListingCard
